<script setup>
import { ref, computed } from 'vue';
import { Head, Link, useForm, usePage } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import BreezeValidationErrors from '@/Components/ValidationErrors.vue';
import SelectSearch from '@/Components/SelectSearch.vue';
import CardBoxModal from '@/Components/CardBoxModal.vue';
import { Icon } from '@iconify/vue';

const page = usePage();
const maxUploadKB = computed(() => {
    const val = Number(page.props.value?.confGlobal?.COMUNICADO_LIMIT_UPLOAD_FILE);
    return (!isNaN(val) && val > 0) ? val : 5000;
});
const maxUploadBytes = computed(() => maxUploadKB.value * 1024);
const maxUploadFormatted = computed(() => {
    if (maxUploadKB.value >= 1024) {
        return (maxUploadKB.value / 1024).toFixed(1).replace('.0', '') + ' MB';
    }
    return maxUploadKB.value + ' KB';
});

const fileErrorMessage = ref('');

const props = defineProps({
    action: {
        type: String,
        default: 'create',
    },
    comunicado: {
        type: Object,
        default: null,
    },
    options: {
        type: Object,
        default: () => ({
            levels: [],
            classrooms: [],
            roles: [],
            students: [],
            collaborators: [],
        }),
    },
});

const isEditing = computed(() => props.action === 'edit');
const pageTitle = computed(() => (isEditing.value ? 'Editar Comunicado' : 'Crear Comunicado'));

const formatDateToISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getToday = () => formatDateToISO(new Date());
const getFutureDate = (days = 30) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return formatDateToISO(d);
};

const form = useForm({
    id: props.comunicado ? props.comunicado.id : null,
    title: props.comunicado ? props.comunicado.title : '',
    description: props.comunicado ? props.comunicado.description : '',
    target_type: props.comunicado ? props.comunicado.target_type : 'all',
    target_id: props.comunicado && props.comunicado.target_id ? props.comunicado.target_id : (props.comunicado?.target_type === 'level' ? 0 : null),
    file: null,
    remove_file: false,
    show_image_in_body: props.comunicado ? Boolean(props.comunicado.show_image_in_body) : false,
    start_date: props.comunicado && props.comunicado.start_date ? props.comunicado.start_date : getToday(),
    end_date: props.comunicado && props.comunicado.end_date ? props.comunicado.end_date : getFutureDate(30),
    requires_acceptance: props.comunicado ? Boolean(props.comunicado.requires_acceptance) : false,
    acceptance_text: props.comunicado?.acceptance_text || 'He leído y acepto el comunicado',
    status: props.comunicado ? Boolean(props.comunicado.status) : true,
});

// Tipos de destino
const targetTypeOptions = [
    { value: 'all', label: 'Todos (Toda la institución)', icon: 'tabler:world' },
    { value: 'level', label: 'Por Nivel Escolar (Padre o Específico)', icon: 'tabler:school' },
    { value: 'classroom', label: 'Por Aula', icon: 'tabler:chalkboard' },
    { value: 'student', label: 'Por Estudiante', icon: 'tabler:user' },
    { value: 'role', label: 'Por Rol de Colaborador', icon: 'tabler:id-badge-2' },
    { value: 'collaborator', label: 'Por Colaborador Específico', icon: 'tabler:user-star' },
];

const onTargetTypeChange = () => {
    if (form.target_type === 'level') {
        form.target_id = 0; // Por defecto compartido con todos los niveles
    } else {
        form.target_id = null;
    }
};

// Opciones dinámicas para el SelectSearch
const currentTargetOptions = computed(() => {
    switch (form.target_type) {
        case 'level':
            return props.options.levels || [];
        case 'classroom':
            return props.options.classrooms || [];
        case 'student':
            return props.options.students || [];
        case 'role':
            return (props.options.roles || []).filter(r => {
                const id = Number(r.id);
                const lbl = String(r.label || '').toLowerCase();
                return id !== 1 && id !== 8 && id !== 9 &&
                       lbl !== 'sistema' && lbl !== 'estudiante' && lbl !== 'apoderado';
            });
        case 'collaborator':
            return props.options.collaborators || [];
        default:
            return [];
    }
});

const targetSelectPlaceholder = computed(() => {
    switch (form.target_type) {
        case 'level':
            return 'Seleccione un nivel (ej: INICIAL, PRIMARIA, o específico)...';
        case 'classroom':
            return 'Seleccione un aula...';
        case 'student':
            return 'Buscar estudiante por nombre o DNI...';
        case 'role':
            return 'Seleccione un rol...';
        case 'collaborator':
            return 'Buscar colaborador...';
        default:
            return 'Seleccione una opción...';
    }
});

// Manejo de archivos (Drag & Drop, Preview)
const isDragging = ref(false);
const fileInputRef = ref(null);
const localPreviewUrl = ref('');
const localFileName = ref('');
const localFileSize = ref('');
const localFileType = ref('');

const existingFileUrl = computed(() => {
    if (props.comunicado && props.comunicado.file_path && !form.remove_file) {
        return `/documents/${props.comunicado.file_path}`;
    }
    return '';
});

const existingFileType = computed(() => {
    if (props.comunicado && !form.remove_file) {
        return props.comunicado.file_type || 'file';
    }
    return '';
});

const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) processSelectedFile(file);
};

const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) processSelectedFile(file);
};

const processSelectedFile = (file) => {
    fileErrorMessage.value = '';

    if (file.size > maxUploadBytes.value) {
        fileErrorMessage.value = `El archivo "${file.name}" (${formatBytes(file.size)}) supera el límite permitido de ${maxUploadFormatted.value}.`;
        if (fileInputRef.value) fileInputRef.value.value = '';
        return;
    }

    form.file = file;
    form.remove_file = false;
    localFileName.value = file.name;
    localFileSize.value = formatBytes(file.size);

    if (file.type.startsWith('image/')) {
        localFileType.value = 'image';
        const reader = new FileReader();
        reader.onload = (e) => {
            localPreviewUrl.value = e.target.result;
        };
        reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        localFileType.value = 'pdf';
        localPreviewUrl.value = '';
    } else {
        localFileType.value = 'file';
        localPreviewUrl.value = '';
    }
};

const removeAttachedFile = () => {
    form.file = null;
    localFileName.value = '';
    localFileSize.value = '';
    localFileType.value = '';
    localPreviewUrl.value = '';
    fileErrorMessage.value = '';
    if (fileInputRef.value) fileInputRef.value.value = '';

    if (props.comunicado && props.comunicado.file_path) {
        form.remove_file = true;
    }
};

// Modal de Vista Previa
const isLivePreviewOpen = ref(false);
const previewAcceptanceChecked = ref(false);

const openLivePreview = () => {
    previewAcceptanceChecked.value = false;
    isLivePreviewOpen.value = true;
};

// Envío del formulario
const submit = () => {
    if (isEditing.value) {
        form.post(route('comunicados-actualizar'), {
            forceFormData: true,
            preserveScroll: true,
        });
    } else {
        form.post(route('comunicados-registrar'), {
            forceFormData: true,
            preserveScroll: true,
        });
    }
};
</script>

<template>
    <Head :title="pageTitle" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                :title="pageTitle"
                subtitle="Configure los datos del comunicado, su destinatario, adjuntos y requisitos de aceptación."
                icon="tabler:speakerphone"
                color="indigo"
            >
                <template #actions>
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            @click="openLivePreview"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 rounded-xl text-sm font-semibold transition border border-indigo-200 dark:border-indigo-800 shadow-sm"
                        >
                            <Icon icon="tabler:eye" class="w-4 h-4" />
                            Vista Previa
                        </button>
                        <Link
                            :href="route('comunicados-list')"
                            class="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 rounded-xl text-sm font-medium transition"
                        >
                            <Icon icon="tabler:arrow-left" class="w-4 h-4" />
                            Volver al Listado
                        </Link>
                    </div>
                </template>
            </PremiumPageHeader>
        </template>

        <div class="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <BreezeValidationErrors class="mb-6" />

            <form @submit.prevent="submit" class="space-y-6">
                <!-- Tarjeta 1: Información Básica -->
                <div class="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Icon icon="tabler:info-circle" class="w-5 h-5 text-indigo-600" />
                        Información Principal
                    </h3>

                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Título del Comunicado <span class="text-rose-500">*</span>
                            </label>
                            <input
                                v-model="form.title"
                                type="text"
                                required
                                placeholder="Ej: Suspensión de actividades por feriado nacional"
                                class="w-full rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2.5 px-3.5"
                            />
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Contenido o Descripción Detallada
                            </label>
                            <textarea
                                v-model="form.description"
                                rows="5"
                                placeholder="Escriba aquí el cuerpo del comunicado, indicaciones o notas para los destinatarios..."
                                class="w-full rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm p-3.5"
                            ></textarea>
                            <p class="text-xs text-gray-400 mt-1">Este texto se presentará al estudiante/usuario en el widget y en el modal de inicio.</p>
                        </div>
                    </div>
                </div>

                <!-- Tarjeta 2: Destinatarios y Alcance -->
                <div class="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Icon icon="tabler:target" class="w-5 h-5 text-indigo-600" />
                        Destinatarios del Comunicado
                    </h3>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Tipo de Destino <span class="text-rose-500">*</span>
                            </label>
                            <select
                                v-model="form.target_type"
                                @change="onTargetTypeChange"
                                class="w-full rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2.5 px-3"
                            >
                                <option v-for="opt in targetTypeOptions" :key="opt.value" :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>

                        <div v-if="form.target_type !== 'all'">
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Seleccionar Destinatario Específico <span class="text-rose-500">*</span>
                            </label>
                            <SelectSearch
                                v-model="form.target_id"
                                :options="currentTargetOptions"
                                :placeholder="targetSelectPlaceholder"
                                :clearable="form.target_type !== 'level'"
                            />
                            <p v-if="form.target_type === 'level'" class="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                                Nota: Si selecciona un nivel padre (ej. INICIAL), el comunicado llegará a todas sus sub-aulas/niveles hijos.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Tarjeta 3: Vigencia y Fechas -->
                <div class="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Icon icon="tabler:calendar-time" class="w-5 h-5 text-indigo-600" />
                        Vigencia de Publicación
                    </h3>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Fecha de Inicio (Desde)
                            </label>
                            <input
                                v-model="form.start_date"
                                type="date"
                                class="w-full rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2.5 px-3"
                            />
                        </div>

                        <div>
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Fecha de Fin (Hasta)
                            </label>
                            <input
                                v-model="form.end_date"
                                type="date"
                                class="w-full rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2.5 px-3"
                            />
                        </div>
                    </div>
                </div>

                <!-- Tarjeta 4: Archivo Adjunto (PDF o Imagen) -->
                <div class="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Icon icon="tabler:paperclip" class="w-5 h-5 text-indigo-600" />
                        Archivo Adjunto (PDF o Imagen)
                    </h3>

                    <!-- Archivo existente -->
                    <div v-if="existingFileUrl" class="mb-4 p-4 rounded-xl bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400">
                                <Icon v-if="existingFileType === 'image'" icon="tabler:photo" class="w-6 h-6" />
                                <Icon v-else-if="existingFileType === 'pdf'" icon="tabler:file-type-pdf" class="w-6 h-6 text-rose-500" />
                                <Icon v-else icon="tabler:file" class="w-6 h-6" />
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-gray-800 dark:text-gray-200">{{ props.comunicado.file_name }}</p>
                                <a :href="existingFileUrl" target="_blank" class="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                                    <Icon icon="tabler:external-link" class="w-3.5 h-3.5" />
                                    Ver archivo actual
                                </a>
                            </div>
                        </div>
                        <button
                            type="button"
                            @click="removeAttachedFile"
                            class="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                        >
                            Eliminar archivo
                        </button>
                    </div>

                    <!-- Archivo local nuevo seleccionado -->
                    <div v-if="form.file" class="mb-4 p-4 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400">
                                <Icon v-if="localFileType === 'image'" icon="tabler:photo" class="w-6 h-6" />
                                <Icon v-else-if="localFileType === 'pdf'" icon="tabler:file-type-pdf" class="w-6 h-6 text-rose-500" />
                                <Icon v-else icon="tabler:file" class="w-6 h-6" />
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-gray-800 dark:text-gray-200">{{ localFileName }}</p>
                                <p class="text-xs text-gray-500">{{ localFileSize }}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            @click="removeAttachedFile"
                            class="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                        >
                            Quitar
                        </button>
                    </div>

                    <!-- Zona Drag & Drop -->
                    <div
                        v-if="!form.file && !existingFileUrl"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleDrop"
                        @click="fileInputRef.click()"
                        :class="[
                            'border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition flex flex-col items-center justify-center gap-2',
                            isDragging ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20' : 'border-gray-300 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-slate-600 bg-gray-50/50 dark:bg-slate-800/30'
                        ]"
                    >
                        <input
                            ref="fileInputRef"
                            type="file"
                            accept="image/*,application/pdf"
                            class="hidden"
                            @change="handleFileSelect"
                        />
                        <div class="w-12 h-12 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center">
                            <Icon icon="tabler:cloud-upload" class="w-6 h-6" />
                        </div>
                        <div>
                            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                <span class="font-semibold text-indigo-600 hover:underline">Haga clic para subir</span> o arrastre un archivo aquí
                            </p>
                            <p class="text-xs text-gray-400 mt-0.5">Formatos permitidos: JPG, PNG, WEBP o PDF (Máx. {{ maxUploadFormatted }})</p>
                        </div>
                    </div>

                    <!-- Mensaje de error de validación de archivo local -->
                    <div v-if="fileErrorMessage" class="mt-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 flex items-center gap-2 text-xs font-semibold text-rose-600 dark:text-rose-400">
                        <Icon icon="tabler:alert-circle" class="w-4 h-4 shrink-0" />
                        <span>{{ fileErrorMessage }}</span>
                    </div>

                    <!-- Opción de mostrar imagen en cuerpo -->
                    <div v-if="(localFileType === 'image' || existingFileType === 'image')" class="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
                        <label class="flex items-center gap-3 cursor-pointer">
                            <input
                                v-model="form.show_image_in_body"
                                type="checkbox"
                                class="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <div>
                                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">Mostrar imagen dentro del cuerpo del comunicado</span>
                                <p class="text-xs text-gray-400">Si se activa, la imagen se desplegará automáticamente visible para los estudiantes en el widget y popup.</p>
                            </div>
                        </label>
                    </div>
                </div>

                <!-- Tarjeta 5: Requerimiento de Aceptación y Estado -->
                <div class="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h3 class="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Icon icon="tabler:checkbox" class="w-5 h-5 text-indigo-600" />
                        Aceptación y Estado
                    </h3>

                    <div class="space-y-4">
                        <label class="flex items-start gap-3 cursor-pointer">
                            <input
                                v-model="form.requires_acceptance"
                                type="checkbox"
                                class="mt-1 w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <div>
                                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">Requiere aceptación obligatoria del usuario</span>
                                <p class="text-xs text-gray-400">
                                    Al iniciar sesión, el estudiante/apoderado verá el comunicado emergente y estará obligado a hacer scroll hasta el final y marcar su aceptación antes de cerrar.
                                </p>
                            </div>
                        </label>

                        <div v-if="form.requires_acceptance" class="pl-7">
                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto de Aceptación
                            </label>
                            <input
                                v-model="form.acceptance_text"
                                type="text"
                                placeholder="Ej: He leído y acepto el comunicado institucional"
                                class="w-full max-w-lg rounded-xl border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm py-2 px-3"
                            />
                        </div>

                        <div class="pt-4 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between">
                            <div>
                                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">Estado de Publicación</span>
                                <p class="text-xs text-gray-400">Si está inactivo, no se mostrará a ningún usuario.</p>
                            </div>
                            <label class="relative inline-flex items-center cursor-pointer">
                                <input v-model="form.status" type="checkbox" class="sr-only peer" />
                                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                <span class="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {{ form.status ? 'Activo' : 'Inactivo' }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Botones de Acción -->
                <div class="flex items-center justify-end gap-3 pt-4">
                    <button
                        type="button"
                        @click="openLivePreview"
                        class="px-5 py-2.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-semibold rounded-xl text-sm transition border border-indigo-200 dark:border-indigo-800 shadow-sm flex items-center gap-2"
                    >
                        <Icon icon="tabler:eye" class="w-4 h-4" />
                        Vista Previa
                    </button>
                    <Link
                        :href="route('comunicados-list')"
                        class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl text-sm transition"
                    >
                        Cancelar
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm shadow-md shadow-indigo-600/20 transition disabled:opacity-50 flex items-center gap-2"
                    >
                        <Icon v-if="form.processing" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                        <Icon v-else icon="tabler:device-floppy" class="w-4 h-4" />
                        {{ isEditing ? 'Guardar Cambios' : 'Publicar Comunicado' }}
                    </button>
                </div>
            </form>
        </div>

        <!-- Modal de Vista Previa en Vivo (Simulación exacta de la vista de estudiante) -->
        <CardBoxModal
            v-model="isLivePreviewOpen"
            title="Vista Previa del Comunicado"
            button="info"
            buttonLabel="Cerrar Vista Previa"
            :has-cancel="false"
        >
            <div class="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <!-- Banner de simulacro -->
                <div class="p-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-800 dark:text-amber-300 flex items-center gap-2">
                    <Icon icon="tabler:info-triangle" class="w-4 h-4 shrink-0 text-amber-600" />
                    <span>Esta es una previsualización de cómo lo visualizarán los destinatarios.</span>
                </div>

                <!-- Tarjeta del comunicado -->
                <div class="border border-indigo-100 dark:border-slate-700 rounded-2xl p-5 bg-gradient-to-br from-indigo-50/40 via-white to-purple-50/30 dark:from-slate-800 dark:to-slate-900 shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
                            <Icon icon="tabler:speakerphone" class="w-3.5 h-3.5" />
                            COMUNICADO OFICIAL
                        </span>
                        <span v-if="form.requires_acceptance" class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300">
                            <Icon icon="tabler:alert-circle" class="w-3.5 h-3.5" />
                            Aceptación Requerida
                        </span>
                    </div>

                    <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {{ form.title || 'Título del Comunicado' }}
                    </h2>

                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 flex items-center gap-2">
                        <Icon icon="tabler:calendar" class="w-3.5 h-3.5" />
                        <span>Vigente: {{ form.start_date || '-' }} al {{ form.end_date || '-' }}</span>
                    </p>

                    <!-- Imagen incrustada si está activa -->
                    <div v-if="form.show_image_in_body && (localPreviewUrl || (existingFileType === 'image' && existingFileUrl))" class="mb-4 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700">
                        <img
                            :src="localPreviewUrl || existingFileUrl"
                            alt="Imagen del comunicado"
                            class="w-full max-h-96 object-contain bg-gray-50 dark:bg-slate-950"
                        />
                    </div>

                    <!-- Contenido textual -->
                    <div class="prose dark:prose-invert max-w-none text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line mb-4">
                        {{ form.description || 'Sin contenido de texto especificado.' }}
                    </div>

                    <!-- Botón PDF si hay archivo no incrustado o PDF -->
                    <div v-if="(localFileType === 'pdf' || existingFileType === 'pdf') || (!form.show_image_in_body && (form.file || existingFileUrl))" class="mt-4 p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div class="w-9 h-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                                <Icon icon="tabler:file-text" class="w-5 h-5" />
                            </div>
                            <div>
                                <p class="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                    {{ localFileName || props.comunicado?.file_name || 'Archivo adjunto' }}
                                </p>
                                <p class="text-[11px] text-gray-400">Haga clic para descargar o visualizar</p>
                            </div>
                        </div>
                        <span class="text-xs text-indigo-600 font-semibold flex items-center gap-1">
                            <Icon icon="tabler:download" class="w-3.5 h-3.5" />
                            Descargar
                        </span>
                    </div>

                    <!-- Simulación de aceptación -->
                    <div v-if="form.requires_acceptance" class="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                        <label class="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                v-model="previewAcceptanceChecked"
                                type="checkbox"
                                class="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <span class="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                {{ form.acceptance_text }}
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </CardBoxModal>
    </BreezeAuthenticatedLayout>
</template>
